import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  scoutApi, ScoutScheduledRun,
  ScoutInstrument, INSTRUMENTS, INSTRUMENT_DISPLAY,
} from '../../../services/api';
import { formatDateTimeUTC6 } from '../../../utils/formatters';

const actionClass = (a: string): string => {
  switch (a) {
    case 'WATCH': return 'agent-action-watch';
    case 'PASS':  return 'agent-action-pass';
    default:      return '';
  }
};

const EntryReviewsLatest: React.FC = () => {
  const [reviews, setReviews] = useState<ScoutScheduledRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReviews(await scoutApi.getLatestEntryReviews());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // Show one card per instrument; if the strategist hasn't fired yet for an
  // instrument, render a placeholder card explaining that.
  const byInstrument: Partial<Record<ScoutInstrument, ScoutScheduledRun>> = {};
  reviews.forEach(r => {
    if (r.instrument && INSTRUMENTS.includes(r.instrument as ScoutInstrument)) {
      byInstrument[r.instrument as ScoutInstrument] = r;
    }
  });

  return (
    <div className="agent-signals-section">
      <div className="agent-signals-header">
        <span className="agent-signals-stats">
          <span className="agent-stat-total">Latest entry review per instrument</span>
        </span>
        <button className="reviews-refresh-btn" onClick={fetch} disabled={loading}>
          {loading ? '...' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className="advisor-error"><strong>Error:</strong> {error}</div>}

      <div className="triggers-list">
        {INSTRUMENTS.map(inst => {
          const r = byInstrument[inst];
          if (!r) {
            return (
              <div key={inst} className="trigger-card">
                <div className="trigger-card-header" style={{ cursor: 'default' }}>
                  <div className="trigger-card-title-row">
                    <span className="instrument-badge">{INSTRUMENT_DISPLAY[inst]}</span>
                    <strong className="trigger-name">No entry review yet</strong>
                  </div>
                  <div className="trigger-card-meta-row">
                    <span className="trigger-meta trigger-meta-dim">
                      Strategist hasn't fired in ENTRY mode for {inst} yet — waiting on next 15-min tick.
                    </span>
                  </div>
                </div>
              </div>
            );
          }

          const expanded = expandedId === r.run_id;
          return (
            <div key={r.run_id} className={`trigger-card ${actionClass(r.action)}`}>
              <div
                className="trigger-card-header"
                onClick={() => setExpandedId(expanded ? null : r.run_id)}
                role="button"
              >
                <div className="trigger-card-title-row">
                  <span className="instrument-badge">{INSTRUMENT_DISPLAY[inst]}</span>
                  <span className={`exit-decision-chip ${actionClass(r.action)}`}>
                    {r.action}
                  </span>
                  {r.direction && (
                    <span className={`dir-tag ${r.direction === 'BUY' ? 'dir-buy' : 'dir-sell'}`}>
                      {r.direction}
                    </span>
                  )}
                  <strong className="trigger-name">
                    {r.setup_type ?? 'No setup'}
                  </strong>
                  {r.confidence != null && (
                    <span className="trigger-meta">conf {Math.round(r.confidence * 100)}%</span>
                  )}
                </div>
                <div className="trigger-card-meta-row">
                  {r.placement_status && (
                    <span className="trigger-meta">{r.placement_status}</span>
                  )}
                  {r.latency_ms != null && (
                    <span className="trigger-meta">{(r.latency_ms / 1000).toFixed(0)}s</span>
                  )}
                  {r.tools_used != null && (
                    <span className="trigger-meta">{r.tools_used} tools</span>
                  )}
                  <span className="trigger-meta trigger-meta-dim">
                    {formatDateTimeUTC6(r.ran_at)}
                  </span>
                  <span className="trigger-card-chevron">{expanded ? '▾' : '▸'}</span>
                </div>
              </div>

              {expanded && (
                <div className="trigger-card-body">
                  {r.plain_english_summary && (
                    <div className="trigger-section">
                      <div className="trigger-section-title">Plain English</div>
                      <div className="advisor-markdown">
                        <ReactMarkdown>{r.plain_english_summary}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                  {r.reasoning && (
                    <div className="trigger-section">
                      <div className="trigger-section-title">Technical reasoning</div>
                      <div className="trigger-reasoning">{r.reasoning}</div>
                    </div>
                  )}
                  <div className="trigger-section">
                    <div className="trigger-meta trigger-meta-dim">
                      run_id: <code>{r.run_id}</code>
                      {r.trade_id && <> · trade_id: <code>{r.trade_id}</code></>}
                      {r.model && <> · model: <code>{r.model}</code></>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EntryReviewsLatest;
